import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@project/components/ui/tabs";
import type { Field } from "@project/admin/components/resource-table";
import { tiffinSevaSchemeService, type TiffinSevaScheme } from "@project/services/admin/tiffin-seva-scheme.service";
import { tiffinSevaDonationService, type TiffinSevaDonation } from "@project/services/admin/tiffin-seva-donation.service";
import { Button } from "@project/components/ui/button";
import { Download, Eye, HandHeart, ListOrdered, Plus } from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { generateReceiptPdf } from "@project/lib/receipt";
import {
  TiffinSevaDonationDetailDialog,
  getTiffinDonationReceiptNo,
  getTiffinDonationPaymentMode,
  getTiffinDonationStatus,
  buildTiffinDonationAddress,
  getTiffinDonationSchemeLabel,
  getTiffinDonationAmount,
  getTiffinDonationPhone,
  getTiffinDonationDonorName,
} from "@project/admin/components/tiffin-seva-donation-detail-dialog";
import { TiffinSevaAddDonationDialog } from "@project/admin/components/tiffin-seva-add-donation-dialog";

const donationFields: Field[] = [
  {
    name: "donor_name",
    label: "Donor Name",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "mobile_number",
    label: "Phone",
    type: "tel",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "scheme_name",
    label: "Scheme",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "amount",
    label: "Amount (₹)",
    type: "number",
    currency: true,
    showInTable: true,
    readOnly: true,
  },
  {
    name: "payment_mode",
    label: "Payment Mode",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "status",
    label: "Status",
    type: "select",
    options: [
      { value: "success", label: "Success" },
      { value: "pending", label: "Pending" },
      { value: "failed", label: "Failed" },
      { value: "cancelled", label: "Cancelled" },
    ],
    showInTable: true,
    readOnly: true,
  },
  {
    name: "created_at",
    label: "Date",
    type: "date",
    showInTable: true,
    readOnly: true,
  },
];

const schemeFields: Field[] = [
  {
    name: "name",
    label: "Scheme Name",
    required: true,
    showInTable: true,
    placeholder: "e.g. 1 Month",
  },
  {
    name: "price",
    label: "Price (₹)",
    required: true,
    type: "number",
    showInTable: true,
    currency: true,
    placeholder: "2500.00",
  },
  {
    name: "is_active",
    label: "Active",
    type: "boolean",
    showInTable: true,
  },
];

function downloadReceipt(row: TiffinSevaDonation) {
  const priceVal = (row as any).scheme_price
    ? `Rs. ${parseFloat((row as any).scheme_price).toLocaleString("en-IN")}`
    : "—";
  const timesVal = (row as any).times ? String((row as any).times) : "—";

  generateReceiptPdf({
    title: "Tiffin Seva Donation Receipt",
    receiptNo: getTiffinDonationReceiptNo(row),
    date: String(row.created_at ?? ""),
    donationScheme: getTiffinDonationSchemeLabel(row),
    donorName: getTiffinDonationDonorName(row),
    donorMobile: getTiffinDonationPhone(row),
    donorEmail: row.email ?? "",
    donorAddress: buildTiffinDonationAddress(row),
    amount: getTiffinDonationAmount(row),
    donationType: "Tiffin Seva",
    paymentMode: getTiffinDonationPaymentMode(row),
    cashOrChequeNo: row.payments?.[0]?.razorpay_payment_id ?? row.payment_reference ?? "",
    preferredTithi: priceVal,
    occasion: timesVal,
  });
}

export default function Page() {
  const [activeTab, setActiveTab] = useState("donations");
  const [selectedDonation, setSelectedDonation] = useState<TiffinSevaDonation | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [listKey, setListKey] = useState(0);

  const handleViewDetails = async (row: TiffinSevaDonation) => {
    setSelectedDonation(null);
    setIsDetailsOpen(true);
    setLoadingDetails(true);
    try {
      const details = await tiffinSevaDonationService.get(row.id);
      if (details) {
        setSelectedDonation({
          ...details,
          scheme_name: row.scheme_name || details.scheme_name,
        });
      } else {
        setSelectedDonation(row);
      }
    } catch {
      setSelectedDonation(row);
      projectToast.error("Could not load full donation details");
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <AdminShell
      title="Tiffin Seva Donations & Schemes"
      subtitle="Manage donor records and multi-day tiffin meal schemes"
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} variant="line" className="space-y-4">
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <TabsList>
            <TabsTrigger value="donations" className="shrink-0">
              <HandHeart className="h-4 w-4 shrink-0" />
              Donation List
            </TabsTrigger>
            <TabsTrigger value="schemes" className="shrink-0">
              <ListOrdered className="h-4 w-4 shrink-0" />
              Tiffin Seva Schemes
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="donations">
          <ApiResourceTable<TiffinSevaDonation>
            key={listKey}
            title="Donation"
            fields={donationFields}
            searchFields={["donor_name", "email", "mobile_number", "scheme_name"]}
            defaultValues={{}}
            csvFileName="tiffin-seva-donations"
            toolbarActions={
              <Button
                type="button"
                size="sm"
                className="flex-1 gap-1 rounded-xl sm:flex-none"
                onClick={() => setIsAddOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Add Donation
              </Button>
            }
            list={async query => {
              const result = await tiffinSevaDonationService.listPage(query);
              try {
                const schemesRes = await tiffinSevaSchemeService.listPage({ page_size: 100 });
                const schemesList = schemesRes.data;
                return {
                  ...result,
                  data: result.data.map(row => {
                    const found = schemesList.find(s => s.id === Number((row as any).scheme));
                    return {
                      ...row,
                      scheme_name: found ? found.name : (row.scheme_name ?? `Scheme #${(row as any).scheme}`),
                    };
                  }),
                };
              } catch (err) {
                console.error("Failed to load schemes for mapping:", err);
                return result;
              }
            }}
            renderRowActions={row => (
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-8 p-0"
                  title="View Details"
                  onClick={() => void handleViewDetails(row)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5"
                  title="Download Receipt"
                  onClick={() => downloadReceipt(row)}
                >
                  <Download className="h-4 w-4" />
                  <span className="text-xs">Receipt</span>
                </Button>
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="schemes">
          <ApiResourceTable<TiffinSevaScheme>
            title="Scheme"
            fields={schemeFields}
            searchFields={["name"]}
            defaultValues={{ is_active: true, price: "0" }}
            csvFileName="tiffin-seva-schemes"
            list={query => tiffinSevaSchemeService.listPage(query)}
            create={payload =>
              tiffinSevaSchemeService.create({
                name: String(payload.name),
                price: String(payload.price),
                is_active: Boolean(payload.is_active),
              })
            }
            update={(id, payload) =>
              tiffinSevaSchemeService.update(id, {
                name: payload.name !== undefined ? String(payload.name) : undefined,
                price: payload.price !== undefined ? String(payload.price) : undefined,
                is_active: payload.is_active !== undefined ? Boolean(payload.is_active) : undefined,
              })
            }
            remove={id => tiffinSevaSchemeService.delete(id)}
          />
        </TabsContent>
      </Tabs>

      <TiffinSevaDonationDetailDialog
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        donation={selectedDonation}
        loading={loadingDetails}
        onDownloadReceipt={downloadReceipt}
      />

      <TiffinSevaAddDonationDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        onSuccess={() => setListKey(key => key + 1)}
      />
    </AdminShell>
  );
}

